import React, { useState, useEffect } from "react";
import {
  FileText,
  Contact,
  Users,
  X,
  Shield,
  FileWarning,
  ChevronLeft,
  ChevronRight,
  Layout,
  DollarSign,
  BarChart3,
  Receipt,
  Calculator,
  Clock,
  Hourglass,
  MessageSquare,
  Layers,
  CreditCard,
  Mail,
  Settings,
  TrendingUp,
  Target,
} from "lucide-react";
import { useUserRole } from "../hooks/useUserRoleSecure";
import { useAuth } from "../contexts/AuthContext";
import { generatePageUrl } from "../utils/urlManager";
import "./Sidebar.css";

const Sidebar = ({ currentPage, onPageChange, isOpen, onToggle }) => {
  const {
    canAccessAdmin,
    canAccessOperationsDashboards,
    canAccessModelBuilding,
    canAccessOfficeAdmin,
    canAccessTakeoffs,
    isTakeoffs,
  } = useUserRole();
  const { user } = useAuth();

  // Profit Analysis is restricted to adam@ab-civil.com only
  const canAccessProfitAnalysis = user?.email === 'adam@ab-civil.com';

  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Load collapsed state from localStorage
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "true";
  });

  // Save collapsed state to localStorage - MUST be before any conditional returns
  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", isCollapsed);
    // Notify parent component about collapse state change
    document.dispatchEvent(
      new CustomEvent("sidebarCollapse", { detail: { isCollapsed } }),
    );
  }, [isCollapsed]);

  const handleItemClick = (page, event) => {
    // Check if Ctrl or Cmd key is pressed (for Mac compatibility)
    if (event.ctrlKey || event.metaKey) {
      // Open in new tab
      const url = generatePageUrl(page);
      window.open(url, '_blank');
      return;
    }

    // Normal navigation
    onPageChange(page);
    // On mobile, close sidebar after selection
    if (window.innerWidth < 1024) {
      onToggle();
    }
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Special case for Takeoffs role - show Feedback first, then Takeoffs and Contacts
  if (isTakeoffs) {
    const sidebarItems = [
      { name: "Feedback", page: "feedback", icon: <MessageSquare size={20} /> },
      { name: "Takeoffs", page: "takeoffs", icon: <Calculator size={20} /> },
      { name: "Contacts", page: "contacts", icon: <Contact size={20} /> },
      { name: "Clients", page: "clients", icon: <Users size={20} /> },
    ];

    // Return early for Takeoffs users with limited navigation
    return (
      <>
        {/* Mobile overlay */}
        {isOpen && window.innerWidth < 1024 && (
          <div className="sidebar-overlay" onClick={onToggle} />
        )}

        <aside
          className={`sidebar ${isOpen ? "open" : ""} ${isCollapsed ? "collapsed" : ""}`}
        >
          {/* Mobile close button */}
          <button className="sidebar-close-mobile" onClick={onToggle}>
            <X size={24} />
          </button>

          <div className="sidebar-header">
            {!isCollapsed && <h2>Takeoffs Portal</h2>}
            {/* Collapse toggle button - only on desktop */}
            <button
              className="sidebar-collapse-btn"
              onClick={toggleCollapse}
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? (
                <ChevronRight size={16} />
              ) : (
                <ChevronLeft size={16} />
              )}
            </button>
          </div>

          <nav className="sidebar-nav">
            {sidebarItems.map((item) => (
              <button
                key={item.page}
                className={`sidebar-item ${currentPage === item.page ? "active" : ""}`}
                onClick={(event) => handleItemClick(item.page, event)}
                title={isCollapsed ? item.name : ""}
              >
                {item.icon}
                {!isCollapsed && <span>{item.name}</span>}
              </button>
            ))}
          </nav>
        </aside>
      </>
    );
  }

  // Model Building Team access items (model_building_team and above)
  const modelBuildingItems = canAccessModelBuilding
    ? [
        { name: "Projects", page: "projects", icon: <FileText size={20} /> },
        { name: "Contacts", page: "contacts", icon: <Contact size={20} /> },
        { name: "Clients", page: "clients", icon: <Users size={20} /> },
        {
          name: "Discrepancies",
          page: "discrepancies",
          icon: <FileWarning size={20} />,
        },
        {
          name: "Project Emails",
          page: "project-emails",
          icon: <Mail size={20} />,
        },
        {
          name: "On Hold",
          page: "on-hold-board",
          icon: <Clock size={20} />,
        },
        {
          name: "Project Setup",
          page: "project-setup",
          icon: <Layout size={20} />,
        },
        {
          name: "Subitem Templates",
          page: "subitem-templates-new",
          icon: <Layers size={20} />,
        },
      ]
    : [];

  // Operations Manager access items (operations_manager and above)
  const operationsItems = canAccessOperationsDashboards
    ? [
        {
          name: "Operations Manager",
          page: "operations-dashboard",
          icon: <DollarSign size={20} />,
        },
        {
          name: "Model Building Manager",
          page: "model-building-manager",
          icon: <Hourglass size={20} />,
        },
        {
          name: "Planner Config",
          page: "planner-config",
          icon: <Settings size={20} />,
        },
        {
          name: "Financial Overview",
          page: "manager-dashboard",
          icon: <BarChart3 size={20} />,
        },
      ]
    : [];

  // Office Admin - only for admin and office_admin roles
  const officeAdminItems = canAccessOfficeAdmin
    ? [
        {
          name: "Office Admin",
          page: "office-admin",
          icon: <Receipt size={20} />,
        },
        {
          name: "Invoicing",
          page: "invoicing",
          icon: <FileText size={20} />,
        },
        {
          name: "Pricing Config",
          page: "pricing",
          icon: <DollarSign size={20} />,
        },
        {
          name: "Payroll Board",
          page: "payroll",
          icon: <CreditCard size={20} />,
        },
        {
          name: "Remote Support Dashboard",
          page: "remote-support",
          icon: <Settings size={20} />,
        },
      ]
    : [];

  // Add Takeoffs for those who have access - appears after Office Admin
  const takeoffsItems = canAccessTakeoffs
    ? [{ name: "Takeoffs", page: "takeoffs", icon: <Calculator size={20} /> }]
    : [];

  // Profit Analysis - restricted to adam@ab-civil.com only
  const profitAnalysisItems = canAccessProfitAnalysis
    ? [{ name: "Profit Analysis", page: "profit-analysis", icon: <TrendingUp size={20} /> }]
    : [];

  // KPI Board - available to all model building team and above
  const kpiBoardItems = canAccessModelBuilding
    ? [{ name: "KPI Board", page: "kpi-board", icon: <Target size={20} /> }]
    : [];

  // Feedback item - always at the top for all users
  const feedbackItem = [
    { name: "Feedback", page: "feedback", icon: <MessageSquare size={20} /> }
  ];

  // Combine all visible navigation items based on role
  const sidebarItems = [
    ...feedbackItem,  // Feedback always first
    ...modelBuildingItems,
    ...operationsItems,
    ...kpiBoardItems,
    ...officeAdminItems,
    ...takeoffsItems,
    ...profitAnalysisItems,
  ];

  // Admin section - only for admin role
  const adminItems = canAccessAdmin
    ? [
        {
          name: "Admin Panel",
          page: "admin",
          icon: <Shield size={20} />,
          isAdmin: true,
        },
        {
          name: "Employees",
          page: "employees",
          icon: <Users size={20} />,
          isAdmin: true,
        },
      ]
    : [];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && window.innerWidth < 1024 && (
        <div className="sidebar-overlay" onClick={onToggle} />
      )}

      <aside
        className={`sidebar ${isOpen ? "open" : ""} ${isCollapsed ? "collapsed" : ""}`}
      >
        {/* Mobile close button */}
        <button className="sidebar-close-mobile" onClick={onToggle}>
          <X size={24} />
        </button>

        <div className="sidebar-header">
          {isCollapsed ? (
            <img
              src="https://db.ab-civil.com/storage/v1/object/public/brand-assets/logos/icon-blue.png"
              alt="AB Civil"
              className="sidebar-logo-icon"
            />
          ) : (
            <img
              src="https://db.ab-civil.com/storage/v1/object/public/brand-assets/logos/primary-white.png"
              alt="AB Civil"
              className="sidebar-logo"
            />
          )}
          {/* Collapse toggle button - only on desktop */}
          <button
            className="sidebar-collapse-btn"
            onClick={toggleCollapse}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronRight size={16} />
            ) : (
              <ChevronLeft size={16} />
            )}
          </button>
        </div>

        <nav className="sidebar-nav">
          {sidebarItems.map((item) => (
            <button
              key={item.page}
              className={`sidebar-item ${currentPage === item.page ? "active" : ""}`}
              onClick={(event) => handleItemClick(item.page, event)}
              title={isCollapsed ? item.name : ""}
            >
              {item.icon}
              {!isCollapsed && <span>{item.name}</span>}
            </button>
          ))}

          {/* Admin section */}
          {adminItems.length > 0 && (
            <>
              {!isCollapsed && (
                <div className="sidebar-divider">
                  <span>Administration</span>
                </div>
              )}
              {isCollapsed && <div className="sidebar-divider collapsed" />}
              {adminItems.map((item) => (
                <button
                  key={item.page}
                  className={`sidebar-item admin-item ${currentPage === item.page ? "active" : ""}`}
                  onClick={(event) => handleItemClick(item.page, event)}
                  title={isCollapsed ? item.name : ""}
                >
                  {item.icon}
                  {!isCollapsed && <span>{item.name}</span>}
                </button>
              ))}
            </>
          )}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
